import { useEffect, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { AdminConfig, AdminSetting } from './api'

type FieldKind = 'int' | 'float' | 'bool' | 'enum' | 'url' | 'text' | 'secret' | 'json'

type FieldSpec = {
  key: string
  label: string
  hint?: string
  kind: FieldKind
  disabled?: boolean
  defaultValue?: unknown
  min?: number
  max?: number
  step?: number
  unit?: string
  options?: { value: string; label: string }[]
}

type SectionSpec = {
  id: string
  title: string
  hint?: string
  fields: FieldSpec[]
}

const DEFAULT_BILLING_PLANS = JSON.stringify([
  {
    id: 'free',
    enabled: true,
    sort_order: 10,
    name: 'Free',
    kicker: 'Start here',
    badge: '',
    description: 'Preview the options income workflow before upgrading.',
    price_label: '$0',
    price_subtext: 'No card required',
    cta_label: 'Create account',
    stripe_price_id: '',
    highlighted: false,
    features: [
      'Scanner preview',
      'Basic watchlist',
      'Educational options income workflow',
    ],
    footnote: '',
  },
  {
    id: 'pro-monthly',
    enabled: true,
    sort_order: 20,
    name: 'Pro Monthly',
    kicker: 'Most flexible',
    badge: 'Popular',
    description: 'Full workflow for active options income research.',
    price_label: '$19',
    price_subtext: 'per month',
    cta_label: 'Upgrade monthly',
    stripe_price_id: '',
    highlighted: true,
    features: [
      'Full scanner and symbol snapshots',
      'Saved presets, watchlist, alerts, and positions',
      'Analyze, market context, and higher limits',
    ],
    footnote: 'Stripe Price ID controls the actual charge.',
  },
  {
    id: 'pro-yearly',
    enabled: false,
    sort_order: 30,
    name: 'Pro Yearly',
    kicker: 'Best value',
    badge: 'Save',
    description: 'Annual access for users who make NavOption a daily workflow.',
    price_label: '$190',
    price_subtext: 'per year',
    cta_label: 'Upgrade yearly',
    stripe_price_id: '',
    highlighted: false,
    features: [
      'Everything in Pro Monthly',
      'Annual billing',
      'Reserved for launch or campaign pricing',
    ],
    footnote: '',
  },
], null, 2)

export const SETTINGS_SECTIONS: SectionSpec[] = [
  {
    id: 'pro_mode',
    title: 'Pro Mode',
    hint: 'When OFF, every signed-in user has full access under the unified limits below. Stripe checkout returns 409. When ON, the existing free/pro tier-split sections (Scan, Watchlist, Presets, Data freshness) take effect.',
    fields: [
      {
        key: 'subscription.pro_mode_enabled',
        label: 'Pro mode enabled',
        kind: 'bool',
        hint: 'Master switch. OFF = unified access for everyone. ON = existing free/pro tier split.',
      },
      { key: 'quota.scan_limit',       label: 'Scan rows (unified)',         kind: 'int', min: 1, max: 1000 },
      { key: 'quota.symbol_row_limit', label: 'Symbol chain rows (unified)', kind: 'int', min: 1, max: 2000 },
      { key: 'quota.watchlist_max',    label: 'Watchlist max (unified)',     kind: 'int', min: 1, max: 1000 },
      { key: 'quota.presets_max',      label: 'Presets max (unified)',       kind: 'int', min: 1, max: 500 },
      { key: 'quota.positions_max',    label: 'Positions max (unified)',     kind: 'int', min: 1, max: 1000 },
    ],
  },
  {
    id: 'scan',
    title: 'Scan',
    hint: 'Option scan API limits and earnings gate.',
    fields: [
      {
        key: 'scan.default_side', label: 'Default side', kind: 'enum',
        options: [
          { value: 'put', label: 'Put' },
          { value: 'call', label: 'Call' },
          { value: 'both', label: 'Both' },
        ],
      },
      { key: 'scan.free_limit', label: 'Free scan limit', kind: 'int', min: 1, max: 500 },
      { key: 'scan.pro_limit', label: 'Pro scan limit', kind: 'int', min: 1, max: 1000 },
      {
        key: 'scan.min_days_to_earnings', label: 'Block scans within N days of earnings',
        kind: 'int', min: 0, max: 30, unit: 'days',
        hint: 'Symbols with earnings within this window are skipped.',
      },
    ],
  },
  {
    id: 'watchlist',
    title: 'Watchlist',
    fields: [
      { key: 'watchlist.free_limit', label: 'Free watchlist limit', kind: 'int', min: 1, max: 200 },
    ],
  },
  {
    id: 'presets',
    title: 'Presets',
    fields: [
      { key: 'presets.free_limit', label: 'Free preset limit', kind: 'int', min: 1, max: 50 },
    ],
  },
  {
    id: 'data_freshness',
    title: 'Data freshness',
    hint: 'Quote websocket delay applied per tier. Set delay to 0 for live.',
    fields: [
      { key: 'data_freshness.free.quotes.delay_seconds', label: 'Free WS delay', kind: 'int', min: 0, max: 86400, unit: 'sec' },
      { key: 'data_freshness.free.quotes.ws_enabled', label: 'Free WS enabled', kind: 'bool' },
      { key: 'data_freshness.pro.quotes.delay_seconds', label: 'Pro WS delay', kind: 'int', min: 0, max: 86400, unit: 'sec' },
      { key: 'data_freshness.pro.quotes.ws_enabled', label: 'Pro WS enabled', kind: 'bool' },
    ],
  },
  {
    id: 'push',
    title: 'Push',
    hint: 'Global delivery controls. User-level preferences stay visible on each user detail page.',
    fields: [
      { key: 'push.enabled', label: 'Push delivery enabled', kind: 'bool', hint: 'Emergency kill switch for APNs fan-out.' },
    ],
  },
  {
    id: 'billing',
    title: 'Billing',
    hint: 'Stripe web checkout settings. Secrets are stored masked after save and read by the API at request time.',
    fields: [
      { key: 'billing.stripe.enabled', label: 'Stripe billing enabled', kind: 'bool', defaultValue: false },
      { key: 'billing.web_app_url', label: 'Web app URL', kind: 'url', defaultValue: 'https://navoption.com' },
      {
        key: 'billing.stripe.price_id_pro_monthly',
        label: 'Legacy Pro monthly price ID',
        kind: 'text',
        hint: 'Fallback only. Prefer billing.plans below for all new pricing cards.',
      },
      {
        key: 'billing.plans',
        label: 'Public pricing plans',
        kind: 'json',
        defaultValue: DEFAULT_BILLING_PLANS,
        hint: 'JSON array rendered by /pricing. Keep id stable; set stripe_price_id for paid plans; disable plans instead of deleting them during campaigns.',
      },
      {
        key: 'billing.stripe.restricted_key',
        label: 'Restricted API key',
        kind: 'secret',
        hint: 'Use a restricted key with Checkout Session, Customer, Customer Portal, and subscription read permissions.',
      },
      {
        key: 'billing.stripe.webhook_secret',
        label: 'Webhook signing secret',
        kind: 'secret',
        hint: 'The whsec_ signing secret for /v1/billing/stripe/webhook.',
      },
      {
        key: 'billing.stripe.test_mode',
        label: 'Sandbox (test mode) — outbound checkout',
        kind: 'bool',
        defaultValue: false,
        hint: 'When ON: /v1/billing/checkout and /v1/billing/portal use the test_* keys + billing.plans.test. Webhook still accepts BOTH live and test signatures so existing live customers are unaffected.',
      },
      {
        key: 'billing.stripe.test_restricted_key',
        label: 'TEST Restricted API key',
        kind: 'secret',
        hint: 'rk_test_... — for sandbox testing. Only used when sandbox toggle is ON above.',
      },
      {
        key: 'billing.stripe.test_webhook_secret',
        label: 'TEST Webhook signing secret',
        kind: 'secret',
        hint: 'whsec_... from a Test-mode webhook endpoint. Tried after the live secret on every incoming webhook.',
      },
      {
        key: 'billing.stripe.test_price_id_pro_monthly',
        label: 'TEST monthly price ID',
        kind: 'text',
        hint: 'price_... in test mode. Fallback when billing.plans.test is empty.',
      },
      {
        key: 'billing.plans.test',
        label: 'TEST pricing plans',
        kind: 'json',
        hint: 'JSON array same shape as billing.plans, but with test mode price IDs. Used only when sandbox toggle is ON.',
      },
    ],
  },
  {
    id: 'calendar',
    title: 'Catalyst Calendar',
    hint: 'Event feed combining earnings, ex-div, option expiries, and macro events.',
    fields: [
      { key: 'calendar.enabled', label: 'Feature enabled', kind: 'bool' },
      { key: 'calendar.unified_lookahead_days', label: 'Pro-mode-off lookahead (days)', kind: 'int', min: 1, max: 365 },
      { key: 'calendar.free_lookahead_days', label: 'Free-tier lookahead (days, pro-mode-on)', kind: 'int', min: 1, max: 90 },
      { key: 'calendar.pro_lookahead_days', label: 'Pro-tier lookahead (days, pro-mode-on)', kind: 'int', min: 1, max: 365 },
      { key: 'calendar.free_include_macro', label: 'Free tier sees macro events (pro-mode-on)', kind: 'bool' },
      {
        key: 'events.macro_calendar',
        label: 'Macro events (JSON array)',
        kind: 'json',
        hint: 'Array of {date, kind, label, impact}. Example: [{"date":"2026-06-12","kind":"FOMC","label":"FOMC Meeting","impact":"H"}]',
      },
    ],
  },
  {
    id: 'iv_evaluation',
    title: 'IV Evaluation Card',
    hint: 'Analyze card showing ATM term structure + IV vs realized volatility.',
    fields: [
      { key: 'iv_evaluation.enabled', label: 'Feature enabled', kind: 'bool' },
      { key: 'iv_evaluation.free_show_term', label: 'Free tier sees term structure (pro-mode-on)', kind: 'bool' },
      { key: 'iv_evaluation.free_rv_windows', label: 'Free tier RV rows shown (1-3, pro-mode-on)', kind: 'int', min: 1, max: 3 },
    ],
  },
  {
    id: 'earnings_card',
    title: 'Earnings-Week Card',
    hint: 'Position-aware earnings prep card shown in Analyze.',
    fields: [
      { key: 'earnings_card.enabled', label: 'Feature enabled', kind: 'bool' },
      { key: 'earnings_card.free_visible', label: 'Free tier sees card (pro-mode-on)', kind: 'bool' },
      { key: 'earnings_card.pro_visible', label: 'Pro tier sees card (pro-mode-on)', kind: 'bool' },
    ],
  },
  {
    id: 'analysis',
    title: 'Analysis Signals',
    hint: 'Stored signal thresholds for Analyze, UOA detection, and insider ingestion.',
    fields: [
      { key: 'analyze.disagreement.medium_threshold', label: 'Medium disagreement', kind: 'float', min: 0, max: 1, step: 0.01 },
      { key: 'analyze.disagreement.high_threshold', label: 'High disagreement', kind: 'float', min: 0, max: 1, step: 0.01 },
      { key: 'uoa.detection.min_multiple', label: 'UOA min multiple', kind: 'float', min: 1, max: 100, step: 0.1 },
      { key: 'uoa.detection.min_today_volume', label: 'UOA min volume', kind: 'int', min: 1, max: 100000 },
      { key: 'uoa.detection.baseline_days', label: 'UOA baseline days', kind: 'int', min: 3, max: 30, unit: 'days' },
      { key: 'uoa.detection.min_otm_pct', label: 'UOA min OTM', kind: 'float', min: 0, max: 1, step: 0.01, unit: 'pct' },
      { key: 'insider.fetcher.universe_size', label: 'Insider universe', kind: 'int', min: 1, max: 5000, unit: 'symbols' },
    ],
  },
]

const LLM_INTERFACE_MODES = [
  { value: 'openai_compatible', label: 'OpenAI-compatible chat', hint: '/chat/completions compatible providers, proxies, gateways, and model routers.' },
  { value: 'anthropic_messages', label: 'Anthropic Messages', hint: 'Claude-style /v1/messages API shape.' },
  { value: 'gemini_generate_content', label: 'Gemini generateContent', hint: 'Google Gemini generateContent API shape.' },
  { value: 'ollama_openai', label: 'Ollama / local OpenAI-compatible', hint: 'Local or LAN OpenAI-compatible endpoint.' },
  { value: 'custom_http', label: 'Custom HTTP adapter', hint: 'Reserved for a custom request/response adapter in the runtime.' },
]

const FALLBACK_REASONS = [
  { value: 'timeout', label: 'Timeout' },
  { value: 'rate_limit', label: 'Rate limit' },
  { value: 'server_error', label: '5xx' },
  { value: 'invalid_json', label: 'Bad JSON' },
]

const LLM_GLOBAL_FIELDS: FieldSpec[] = [
  { key: 'llm.routing.enabled', label: 'Enable multi-provider routing', kind: 'bool', defaultValue: true },
  {
    key: 'llm.routing.mode', label: 'Routing mode', kind: 'enum', defaultValue: 'priority_fallback',
    options: [
      { value: 'priority_fallback', label: 'Priority fallback' },
      { value: 'cost_guarded', label: 'Cost guarded' },
      { value: 'manual_primary', label: 'Manual primary only' },
    ],
  },
  { key: 'llm.routing.timeout_seconds', label: 'Provider timeout', kind: 'int', min: 5, max: 180, unit: 'sec', defaultValue: 60 },
  { key: 'llm.routing.retry_per_provider', label: 'Retries per provider', kind: 'int', min: 0, max: 5, defaultValue: 1 },
  { key: 'llm.fallback_on', label: 'Fallback conditions', kind: 'text', defaultValue: 'timeout,rate_limit,server_error,invalid_json' },
  {
    key: 'llm.fallback.default_behavior', label: 'If all providers fail', kind: 'enum', defaultValue: 'rules',
    options: [
      { value: 'rules', label: 'Return rules-only analysis' },
      { value: 'partial', label: 'Return partial LLM output' },
      { value: 'fail_closed', label: 'Fail closed' },
    ],
  },
]

const LEGACY_LLM_FIELDS: FieldSpec[] = [
  { key: 'llm.provider', label: 'Provider', kind: 'text' },
  { key: 'llm.base_url', label: 'Base URL', kind: 'url' },
  { key: 'llm.model', label: 'Model', kind: 'text' },
  { key: 'llm.api_key', label: 'API key', kind: 'secret' },
]

const LLM_STATIC_FIELDS = [
  ...LLM_GLOBAL_FIELDS,
  ...LEGACY_LLM_FIELDS,
]

const STATIC_SETTING_FIELDS = [
  ...SETTINGS_SECTIONS.flatMap((section) => section.fields),
  ...LLM_STATIC_FIELDS,
]

const PROVIDER_KEY_RE = /^llm\.providers\.([A-Za-z0-9_-]+)\.([A-Za-z0-9_]+)$/

type ProviderSeed = {
  label: string
  interfaceMode: string
  enabled: boolean
  priority: number
  baseURL: string
  model: string
}

function providerKey(providerID: string, leaf: string): string {
  return `llm.providers.${providerID}.${leaf}`
}

function providerFields(providerID: string, seed: ProviderSeed): FieldSpec[] {
  return [
    { key: providerKey(providerID, 'enabled'), label: 'Enabled', kind: 'bool', defaultValue: seed.enabled },
    { key: providerKey(providerID, 'priority'), label: 'Priority', kind: 'int', min: 1, max: 99, defaultValue: seed.priority },
    { key: providerKey(providerID, 'label'), label: 'Provider name', kind: 'text', defaultValue: seed.label },
    {
      key: providerKey(providerID, 'interface_mode'),
      label: 'Interface mode',
      kind: 'enum',
      defaultValue: seed.interfaceMode,
      options: LLM_INTERFACE_MODES.map((mode) => ({ value: mode.value, label: mode.label })),
    },
    { key: providerKey(providerID, 'base_url'), label: 'Base URL', kind: 'url', defaultValue: seed.baseURL },
    { key: providerKey(providerID, 'model'), label: 'Model', kind: 'text', defaultValue: seed.model },
    { key: providerKey(providerID, 'api_key'), label: 'API key', kind: 'secret' },
    { key: providerKey(providerID, 'timeout_seconds'), label: 'Timeout override', kind: 'int', min: 0, max: 180, unit: 'sec', defaultValue: 0 },
    { key: providerKey(providerID, 'notes'), label: 'Notes', kind: 'text', defaultValue: '' },
  ]
}

export type SaveItem = { key: string; value: unknown; is_secret: boolean }

export type SettingsPanelProps = {
  config: AdminConfig | null
  onSaveSection: (sectionID: string, items: SaveItem[]) => Promise<void>
}

type DraftMap = Record<string, string>

type BillingPlanDraft = {
  id: string
  enabled: boolean
  sort_order: number
  name: string
  kicker: string
  badge: string
  description: string
  price_label: string
  price_subtext: string
  cta_label: string
  stripe_price_id: string
  highlighted: boolean
  features: string[]
  footnote: string
}

function settingsByKey(config: AdminConfig | null): Record<string, AdminSetting> {
  const out: Record<string, AdminSetting> = {}
  for (const s of config?.settings ?? []) out[s.key] = s
  return out
}

function initialValue(setting: AdminSetting | undefined, kind: FieldKind, defaultValue: unknown = ''): string {
  if (!setting) {
    if (kind === 'bool') return defaultValue === true || defaultValue === 'true' ? 'true' : 'false'
    if (defaultValue === null || defaultValue === undefined) return ''
    return String(defaultValue)
  }
  const v = setting.value
  if (kind === 'secret') return ''
  if (kind === 'bool') return (v === true || v === 'true') ? 'true' : 'false'
  if (kind === 'json') {
    if (typeof v === 'string') return v
    return JSON.stringify(v, null, 2)
  }
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  return String(v)
}

function isDirty(field: FieldSpec, draft: string, baseline: string): boolean {
  if (field.kind === 'secret') return draft !== ''
  return draft !== baseline
}

function valueForSubmit(field: FieldSpec, draft: string): unknown {
  switch (field.kind) {
    case 'int': {
      const n = parseInt(draft, 10)
      return Number.isFinite(n) ? n : 0
    }
    case 'float': {
      const n = parseFloat(draft)
      return Number.isFinite(n) ? n : 0
    }
    case 'bool':
      return draft === 'true'
    case 'secret':
    case 'json':
      try {
        return JSON.parse(draft)
      } catch {
        return draft
      }
    case 'url':
    case 'text':
    case 'enum':
    default:
      return draft
  }
}

function allSettingFields(config: AdminConfig | null): FieldSpec[] {
  return [
    ...STATIC_SETTING_FIELDS,
    ...providerIDsFromConfig(config).flatMap((id, index) => providerFields(id, providerSeed(config, id, index))),
  ]
}

function providerIDsFromConfig(config: AdminConfig | null): string[] {
  const ids = new Set<string>()
  for (const setting of config?.settings ?? []) {
    const match = setting.key.match(PROVIDER_KEY_RE)
    if (match) ids.add(match[1])
  }
  if (ids.size === 0) ids.add(safeProviderID(config?.llm.provider || 'primary'))
  return [...ids]
}

function providerIDsFromDrafts(drafts: DraftMap, config: AdminConfig | null): string[] {
  const ids = new Set(providerIDsFromConfig(config))
  for (const key of Object.keys(drafts)) {
    const match = key.match(PROVIDER_KEY_RE)
    if (match) ids.add(match[1])
  }
  return [...ids]
}

function providerSeed(config: AdminConfig | null, providerID: string, index: number): ProviderSeed {
  const legacyID = safeProviderID(config?.llm.provider || 'primary')
  const label = providerID === legacyID ? config?.llm.provider || 'Primary provider' : providerLabelFromID(providerID)
  return {
    label,
    interfaceMode: 'openai_compatible',
    enabled: index === 0,
    priority: index + 1,
    baseURL: providerID === legacyID ? config?.llm.base_url || '' : '',
    model: providerID === legacyID ? config?.llm.model || '' : '',
  }
}

function providerLabelFromID(providerID: string): string {
  return providerID
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Provider'
}

function safeProviderID(raw: string): string {
  const id = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  return id || 'provider'
}

function uniqueProviderID(raw: string, existing: string[]): string {
  const base = safeProviderID(raw)
  const seen = new Set(existing)
  if (!seen.has(base)) return base
  for (let i = 2; i < 100; i += 1) {
    const next = `${base}-${i}`
    if (!seen.has(next)) return next
  }
  return `${base}-${Date.now()}`
}

export function SettingsPanel({ config, onSaveSection }: SettingsPanelProps) {
  const baseline = useMemo(() => {
    const byKey = settingsByKey(config)
    const out: DraftMap = {}
    for (const field of allSettingFields(config)) {
      out[field.key] = initialValue(byKey[field.key], field.kind, field.defaultValue)
    }
    return out
  }, [config])

  const [drafts, setDrafts] = useState<DraftMap>(baseline)
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    setDrafts(baseline)
  }, [baseline])

  const byKey = settingsByKey(config)

  return (
    <div className="stack">
      <div className="save-bar">
        <div>
          <strong>Admin Settings</strong>
          <span>Per-section saves only push changed fields.</span>
        </div>
      </div>
      {SETTINGS_SECTIONS.map((section) => {
        const dirtyFields = section.fields.filter((f) => isDirty(f, drafts[f.key] ?? '', baseline[f.key] ?? ''))
        const dirty = dirtyFields.length > 0
        const sectionSaving = saving === section.id
        const visibleFields = section.id === 'billing'
          ? section.fields.filter((field) => field.key !== 'billing.plans')
          : section.fields

        const onSave = async () => {
          if (!dirty) return
          setSaving(section.id)
          try {
            const items: SaveItem[] = dirtyFields.map((f) => ({
              key: f.key,
              value: valueForSubmit(f, drafts[f.key] ?? ''),
              is_secret: f.kind === 'secret',
            }))
            await onSaveSection(section.id, items)
          } finally {
            setSaving(null)
          }
        }

        const onDiscard = () => {
          const reset: DraftMap = { ...drafts }
          for (const f of section.fields) reset[f.key] = baseline[f.key] ?? ''
          setDrafts(reset)
        }

        return (
          <section className="panel" key={section.id}>
            <div className="panel-head">
              <div>
                <div className="eyebrow">Stored configuration</div>
                <h2>{section.title}</h2>
              </div>
              <div className="panel-actions">
                {dirty && (
                  <button type="button" className="ghost small" onClick={onDiscard} disabled={sectionSaving}>
                    Discard
                  </button>
                )}
                <button type="button" className="small" onClick={onSave} disabled={!dirty || sectionSaving}>
                  {sectionSaving ? 'Saving...' : dirty ? `Save (${dirtyFields.length})` : 'Saved'}
                </button>
              </div>
            </div>
            {section.hint && <p className="section-hint">{section.hint}</p>}
            <div className="settings-grid">
              {visibleFields.map((field) => (
                <FieldRow
                  key={field.key}
                  field={field}
                  setting={byKey[field.key]}
                  value={drafts[field.key] ?? ''}
                  onChange={(next) => setDrafts((prev) => ({ ...prev, [field.key]: next }))}
                />
              ))}
            </div>
            {section.id === 'billing' && (
              <BillingPlansEditor
                value={drafts['billing.plans'] ?? DEFAULT_BILLING_PLANS}
                onChange={(next) => setDrafts((prev) => ({ ...prev, 'billing.plans': next }))}
              />
            )}
          </section>
        )
      })}
      <LLMSettingsSection
        config={config}
        byKey={byKey}
        baseline={baseline}
        drafts={drafts}
        setDrafts={setDrafts}
        saving={saving}
        setSaving={setSaving}
        onSaveSection={onSaveSection}
      />
    </div>
  )
}

function BillingPlansEditor({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const plans = parseBillingPlans(value)
  const enabledCount = plans.filter((plan) => plan.enabled).length
  const paidCount = plans.filter((plan) => plan.enabled && plan.stripe_price_id.trim()).length

  const writePlans = (next: BillingPlanDraft[]) => {
    onChange(JSON.stringify(next.map(cleanBillingPlan), null, 2))
  }

  const updatePlan = (index: number, patch: Partial<BillingPlanDraft>) => {
    writePlans(plans.map((plan, i) => (i === index ? { ...plan, ...patch } : plan)))
  }

  const addPlan = () => {
    const nextOrder = Math.max(0, ...plans.map((plan) => plan.sort_order || 0)) + 10
    const nextID = uniqueBillingPlanID('pro-plan', plans.map((plan) => plan.id))
    writePlans([
      ...plans,
      {
        id: nextID,
        enabled: false,
        sort_order: nextOrder,
        name: 'New Plan',
        kicker: 'New offer',
        badge: '',
        description: '',
        price_label: '$',
        price_subtext: 'per month',
        cta_label: 'Upgrade',
        stripe_price_id: '',
        highlighted: false,
        features: ['Full scanner access'],
        footnote: '',
      },
    ])
  }

  const duplicatePlan = (index: number) => {
    const source = plans[index]
    const nextID = uniqueBillingPlanID(`${source.id || 'plan'}-copy`, plans.map((plan) => plan.id))
    writePlans([
      ...plans.slice(0, index + 1),
      {
        ...source,
        id: nextID,
        enabled: false,
        highlighted: false,
        sort_order: source.sort_order + 1,
        name: `${source.name} Copy`,
      },
      ...plans.slice(index + 1),
    ])
  }

  const removePlan = (index: number) => {
    writePlans(plans.filter((_, i) => i !== index))
  }

  return (
    <section className="billing-plans">
      <div className="billing-plans-head">
        <div>
          <div className="eyebrow">Public pricing</div>
          <h3>Plans shown on the website</h3>
          <p className="section-hint">
            Edit the cards users see on /pricing. Paid plans need a Stripe Price ID; free plans should leave it blank.
          </p>
        </div>
        <div className="billing-plans-actions">
          <span>{enabledCount} live</span>
          <span>{paidCount} paid</span>
          <button type="button" className="small" onClick={addPlan}>Add plan</button>
        </div>
      </div>
      <div className="billing-plan-list">
        {plans.map((plan, index) => (
          <article className={plan.enabled ? 'billing-plan-card enabled' : 'billing-plan-card'} key={`${plan.id}-${index}`}>
            <div className="billing-plan-card-head">
              <div>
                <div className="eyebrow">{plan.id || 'plan-id'}</div>
                <h4>{plan.name || 'Untitled plan'}</h4>
              </div>
              <div className="billing-plan-flags">
                <label><input type="checkbox" checked={plan.enabled} onChange={(e) => updatePlan(index, { enabled: e.target.checked })} /> Live</label>
                <label><input type="checkbox" checked={plan.highlighted} onChange={(e) => updatePlan(index, { highlighted: e.target.checked })} /> Featured</label>
              </div>
            </div>
            <div className="billing-plan-grid">
              <label><span>Plan ID</span><input value={plan.id} onChange={(e) => updatePlan(index, { id: safeBillingPlanID(e.target.value) })} /></label>
              <label><span>Sort</span><input type="number" value={plan.sort_order} onChange={(e) => updatePlan(index, { sort_order: numericValue(e.target.value, 0) })} /></label>
              <label><span>Name</span><input value={plan.name} onChange={(e) => updatePlan(index, { name: e.target.value })} /></label>
              <label><span>Kicker</span><input value={plan.kicker} onChange={(e) => updatePlan(index, { kicker: e.target.value })} /></label>
              <label><span>Badge</span><input value={plan.badge} placeholder="Popular" onChange={(e) => updatePlan(index, { badge: e.target.value })} /></label>
              <label><span>Price label</span><input value={plan.price_label} placeholder="$19" onChange={(e) => updatePlan(index, { price_label: e.target.value })} /></label>
              <label><span>Price note</span><input value={plan.price_subtext} placeholder="per month" onChange={(e) => updatePlan(index, { price_subtext: e.target.value })} /></label>
              <label><span>CTA</span><input value={plan.cta_label} placeholder="Upgrade" onChange={(e) => updatePlan(index, { cta_label: e.target.value })} /></label>
              <label className="wide"><span>Stripe Price ID</span><input value={plan.stripe_price_id} placeholder="price_..." onChange={(e) => updatePlan(index, { stripe_price_id: e.target.value.trim() })} /></label>
              <label className="wide"><span>Description</span><textarea rows={3} value={plan.description} onChange={(e) => updatePlan(index, { description: e.target.value })} /></label>
              <label className="wide"><span>Features</span><textarea rows={5} value={plan.features.join('\n')} onChange={(e) => updatePlan(index, { features: linesValue(e.target.value) })} /></label>
              <label className="wide"><span>Footnote</span><input value={plan.footnote} placeholder="Cancel anytime." onChange={(e) => updatePlan(index, { footnote: e.target.value })} /></label>
            </div>
            <div className="billing-plan-footer">
              <small>{plan.stripe_price_id ? 'Paid Stripe checkout plan' : 'Free or informational plan'}</small>
              <div>
                <button type="button" className="ghost small" onClick={() => duplicatePlan(index)}>Duplicate</button>
                <button type="button" className="ghost small danger" onClick={() => removePlan(index)}>Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function parseBillingPlans(raw: string): BillingPlanDraft[] {
  try {
    const parsed = JSON.parse(raw || DEFAULT_BILLING_PLANS)
    if (Array.isArray(parsed)) return parsed.map(normalizeBillingPlan)
  } catch {
    // Fall back to defaults; the editor will rewrite valid JSON on the next change.
  }
  return (JSON.parse(DEFAULT_BILLING_PLANS) as unknown[]).map(normalizeBillingPlan)
}

function normalizeBillingPlan(raw: unknown): BillingPlanDraft {
  const item = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    id: safeBillingPlanID(String(item.id || 'plan')),
    enabled: item.enabled !== false,
    sort_order: numericValue(item.sort_order, 0),
    name: String(item.name || ''),
    kicker: String(item.kicker || ''),
    badge: String(item.badge || ''),
    description: String(item.description || ''),
    price_label: String(item.price_label || ''),
    price_subtext: String(item.price_subtext || ''),
    cta_label: String(item.cta_label || ''),
    stripe_price_id: String(item.stripe_price_id || ''),
    highlighted: item.highlighted === true,
    features: Array.isArray(item.features) ? item.features.map(String) : [],
    footnote: String(item.footnote || ''),
  }
}

function cleanBillingPlan(plan: BillingPlanDraft) {
  return {
    ...plan,
    id: safeBillingPlanID(plan.id),
    sort_order: numericValue(plan.sort_order, 0),
    features: plan.features.map((feature) => feature.trim()).filter(Boolean),
  }
}

function safeBillingPlanID(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

function uniqueBillingPlanID(raw: string, existing: string[]): string {
  const base = safeBillingPlanID(raw) || 'plan'
  const seen = new Set(existing)
  if (!seen.has(base)) return base
  for (let i = 2; i < 100; i += 1) {
    const next = `${base}-${i}`
    if (!seen.has(next)) return next
  }
  return `${base}-${Date.now()}`
}

function numericValue(raw: unknown, fallback: number): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10)
  return Number.isFinite(n) ? n : fallback
}

function linesValue(raw: string): string[] {
  return raw.split('\n').map((line) => line.trim()).filter(Boolean)
}

function LLMSettingsSection({
  config,
  byKey,
  baseline,
  drafts,
  setDrafts,
  saving,
  setSaving,
  onSaveSection,
}: {
  config: AdminConfig | null
  byKey: Record<string, AdminSetting>
  baseline: DraftMap
  drafts: DraftMap
  setDrafts: Dispatch<SetStateAction<DraftMap>>
  saving: string | null
  setSaving: Dispatch<SetStateAction<string | null>>
  onSaveSection: (sectionID: string, items: SaveItem[]) => Promise<void>
}) {
  const providerIDs = providerIDsFromDrafts(drafts, config)
  const providerFieldMap = new Map(providerIDs.flatMap((id, index) => (
    providerFields(id, providerSeed(config, id, index)).map((field) => [field.key, field] as const)
  )))
  const llmFields = [...LLM_STATIC_FIELDS, ...providerFieldMap.values()]
  const dirtyFields = llmFields.filter((f) => isDirty(f, drafts[f.key] ?? '', baseline[f.key] ?? ''))
  const dirty = dirtyFields.length > 0
  const sectionSaving = saving === 'llm'
  const enabledProviders = providerIDs
    .filter((id) => drafts[providerKey(id, 'enabled')] === 'true')
    .sort((a, b) => numericDraft(drafts[providerKey(a, 'priority')], 99) - numericDraft(drafts[providerKey(b, 'priority')], 99))
  const primary = enabledProviders[0] ?? null

  const setDraft = (key: string, next: string) => {
    setDrafts((prev) => ({ ...prev, [key]: next }))
  }

  const onDiscard = () => {
    setDrafts((prev) => {
      const reset: DraftMap = {}
      for (const [key, value] of Object.entries(prev)) {
        if (!key.startsWith('llm.')) reset[key] = value
      }
      for (const field of llmFields) {
        if (baseline[field.key] !== undefined) reset[field.key] = baseline[field.key]
      }
      return reset
    })
  }

  const onAddProvider = () => {
    setDrafts((prev) => {
      const currentIDs = providerIDsFromDrafts(prev, config)
      const nextID = uniqueProviderID(`provider-${currentIDs.length + 1}`, currentIDs)
      const seed: ProviderSeed = {
        label: 'New provider',
        interfaceMode: 'openai_compatible',
        enabled: true,
        priority: currentIDs.length + 1,
        baseURL: '',
        model: '',
      }
      const next = { ...prev }
      for (const field of providerFields(nextID, seed)) {
        next[field.key] = initialValue(undefined, field.kind, field.defaultValue)
      }
      return next
    })
  }

  const onRemoveDraftProvider = (providerID: string) => {
    setDrafts((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        if (key.startsWith(`llm.providers.${providerID}.`) && baseline[key] === undefined) delete next[key]
      }
      if (Object.keys(next).some((key) => key.startsWith(`llm.providers.${providerID}.`))) {
        next[providerKey(providerID, 'enabled')] = 'false'
      }
      return next
    })
  }

  const onSave = async () => {
    if (!dirty) return
    setSaving('llm')
    try {
      const items: SaveItem[] = dirtyFields.map((f) => ({
        key: f.key,
        value: valueForSubmit(f, drafts[f.key] ?? ''),
        is_secret: f.kind === 'secret',
      }))
      if (primary) {
        appendChanged(items, 'llm.provider', primary, baseline, false)
        appendChanged(items, 'llm.base_url', drafts[providerKey(primary, 'base_url')] || '', baseline, false)
        appendChanged(items, 'llm.model', drafts[providerKey(primary, 'model')] || '', baseline, false)
        const primaryKey = drafts[providerKey(primary, 'api_key')] ?? ''
        if (primaryKey) appendChanged(items, 'llm.api_key', primaryKey, baseline, true)
      }
      await onSaveSection('llm', items)
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="panel llm-panel">
      <div className="panel-head">
        <div>
          <div className="eyebrow">Stored configuration</div>
          <h2>LLM routing</h2>
        </div>
        <div className="panel-actions">
          {dirty && (
            <button type="button" className="ghost small" onClick={onDiscard} disabled={sectionSaving}>
              Discard
            </button>
          )}
          <button type="button" className="small" onClick={onSave} disabled={!dirty || sectionSaving}>
            {sectionSaving ? 'Saving...' : dirty ? `Save (${dirtyFields.length})` : 'Saved'}
          </button>
        </div>
      </div>

      <div className="llm-overview">
        <div>
          <p className="section-hint">
            Stored provider settings are saved in admin_settings. The legacy primary mirror stays aligned with the lowest-priority enabled provider for the current runtime path.
          </p>
          <div className="llm-chain" aria-label="Effective LLM fallback chain">
            {enabledProviders.length === 0 ? (
              <span className="llm-empty">No provider enabled</span>
            ) : (
              enabledProviders.map((providerID, index) => (
                <div className="llm-chain-node" key={providerID}>
                  <span>{index === 0 ? 'Primary' : `Fallback ${index}`}</span>
                  <strong>{drafts[providerKey(providerID, 'label')] || providerLabelFromID(providerID)}</strong>
                  <small>{interfaceModeLabel(drafts[providerKey(providerID, 'interface_mode')])}</small>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="runtime-card">
          <span>Runtime mirror</span>
          <strong>{config?.llm.model || '-'}</strong>
          <small>{config?.llm.api_key_configured ? 'API key configured' : 'API key missing'} · {config?.llm.base_url || 'default base URL'}</small>
        </div>
      </div>

      <div className="llm-routing-grid">
        <div className="llm-policy">
          <h3>Routing policy</h3>
          <div className="settings-grid compact-settings">
            {LLM_GLOBAL_FIELDS.filter((field) => field.key !== 'llm.fallback_on').map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                setting={byKey[field.key]}
                value={drafts[field.key] ?? ''}
                onChange={(next) => setDraft(field.key, next)}
              />
            ))}
          </div>
          <div className="fallback-field">
            <span className="eyebrow">Fallback conditions</span>
            <div className="fallback-options">
              {FALLBACK_REASONS.map((reason) => {
                const active = fallbackValues(drafts['llm.fallback_on']).includes(reason.value)
                return (
                  <button
                    key={reason.value}
                    type="button"
                    className={active ? 'chip active' : 'chip'}
                    onClick={() => setDraft('llm.fallback_on', toggleFallbackValue(drafts['llm.fallback_on'], reason.value))}
                  >
                    {reason.label}
                  </button>
                )
              })}
            </div>
            <small className="secret-preview">Stored as {drafts['llm.fallback_on'] || '-'}</small>
          </div>
        </div>

        <div className="llm-providers">
          <div className="provider-list-head">
            <div>
              <h3>Providers</h3>
              <p className="section-hint">Add providers one by one. The lowest enabled priority becomes primary.</p>
            </div>
            <button type="button" className="small" onClick={onAddProvider}>Add provider</button>
          </div>
          {providerIDs.map((providerID, index) => (
            <ProviderCard
              key={providerID}
              providerID={providerID}
              index={index}
              byKey={byKey}
              baseline={baseline}
              drafts={drafts}
              onChange={setDraft}
              onRemove={() => onRemoveDraftProvider(providerID)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function ProviderCard({
  providerID,
  index,
  byKey,
  baseline,
  drafts,
  onChange,
  onRemove,
}: {
  providerID: string
  index: number
  byKey: Record<string, AdminSetting>
  baseline: DraftMap
  drafts: DraftMap
  onChange: (key: string, value: string) => void
  onRemove: () => void
}) {
  const fields = providerFields(providerID, providerSeed(null, providerID, index))
  const enabled = drafts[providerKey(providerID, 'enabled')] === 'true'
  const label = drafts[providerKey(providerID, 'label')] || providerLabelFromID(providerID)

  return (
    <article className={enabled ? 'provider-card enabled' : 'provider-card'}>
      <div className="provider-card-head">
        <div>
          <div className="eyebrow">{providerID}</div>
          <h3>{label}</h3>
        </div>
        <div className="provider-card-actions">
          <span className={enabled ? 'pill pro' : 'pill'}>{enabled ? 'ENABLED' : 'OFF'}</span>
          <button type="button" className="ghost small" onClick={onRemove}>
            {Object.keys(baseline).some((key) => key.startsWith(`llm.providers.${providerID}.`)) ? 'Disable' : 'Remove'}
          </button>
        </div>
      </div>
      <div className="provider-grid">
        {fields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            setting={byKey[field.key]}
            value={drafts[field.key] ?? ''}
            onChange={(next) => onChange(field.key, next)}
          />
        ))}
      </div>
    </article>
  )
}

function appendChanged(items: SaveItem[], key: string, value: unknown, baseline: DraftMap, isSecret: boolean) {
  if (items.some((item) => item.key === key)) return
  if (String(value ?? '') === (baseline[key] ?? '')) return
  items.push({ key, value, is_secret: isSecret })
}

function numericDraft(value: string | undefined, fallback: number): number {
  const n = parseInt(value ?? '', 10)
  return Number.isFinite(n) ? n : fallback
}

function interfaceModeLabel(value: string | undefined): string {
  return LLM_INTERFACE_MODES.find((mode) => mode.value === value)?.label || value || 'Interface mode not set'
}

function fallbackValues(raw: string | undefined): string[] {
  return (raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

function toggleFallbackValue(raw: string | undefined, value: string): string {
  const values = fallbackValues(raw)
  if (values.includes(value)) return values.filter((item) => item !== value).join(',')
  return [...values, value].join(',')
}

function FieldRow({ field, setting, value, onChange }: {
  field: FieldSpec
  setting: AdminSetting | undefined
  value: string
  onChange: (next: string) => void
}) {
  return (
    <label className="setting-field" key={field.key}>
      <span>{field.label}</span>
      <FieldControl field={field} setting={setting} value={value} onChange={onChange} />
      <small>
        {field.key}
        {setting?.source ? ` · ${setting.source}` : ' · default'}
        {field.unit ? ` · ${field.unit}` : ''}
      </small>
      {field.hint && <small className="field-hint">{field.hint}</small>}
    </label>
  )
}

function FieldControl({ field, setting, value, onChange }: {
  field: FieldSpec
  setting: AdminSetting | undefined
  value: string
  onChange: (next: string) => void
}) {
  switch (field.kind) {
    case 'int':
    case 'float':
      return (
        <input
          type="number"
          inputMode={field.kind === 'int' ? 'numeric' : 'decimal'}
          value={value}
          min={field.min}
          max={field.max}
          step={field.step ?? (field.kind === 'int' ? 1 : 0.01)}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'bool':
      return (
        <label className="switch">
          <input
            type="checkbox"
            checked={value === 'true'}
            disabled={field.disabled}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
          <span />
        </label>
      )
    case 'enum':
      return (
        <select value={value} disabled={field.disabled} onChange={(e) => onChange(e.target.value)}>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )
    case 'url':
      return (
        <input type="url" value={value} placeholder="https://..." disabled={field.disabled} onChange={(e) => onChange(e.target.value)} />
      )
    case 'secret': {
      const preview = typeof setting?.value === 'string' && setting.value ? setting.value : 'not set'
      return (
        <>
          <input
            type="password"
            value={value}
            placeholder="Leave empty to keep current"
            autoComplete="new-password"
            disabled={field.disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <small className="secret-preview">Current: {preview}</small>
        </>
      )
    }
    case 'json':
      return (
        <textarea
          rows={6}
          value={value}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
        />
      )
    case 'text':
    default:
      return (
        <input type="text" value={value} disabled={field.disabled} onChange={(e) => onChange(e.target.value)} />
      )
  }
}
